import { AlertTriangle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { MermaidContent, ScriptElement } from '@/lib/schema.types';

interface Props {
  element: ScriptElement;
  /** Fill the parent box (freeform / stage). */
  fill?: boolean;
}

// Mermaid is heavy, so it's loaded lazily and only once (code-split chunk).
// npm package — not a CDN — so it works offline once bundled.
let mermaidPromise: Promise<typeof import('mermaid')['default']> | null = null;
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((m) => {
      m.default.initialize({
        startOnLoad: false,
        securityLevel: 'strict',
        theme: 'default',
        fontFamily: 'inherit',
      });
      return m.default;
    });
  }
  return mermaidPromise;
}

// Unique id per render — mermaid requires a fresh DOM id each time.
let renderSeq = 0;

export function MermaidElement({ element, fill }: Props) {
  const content = element.content as MermaidContent | undefined;
  const source = (content?.source ?? '').trim();
  const caption = content?.caption?.trim();

  const [svg, setSvg] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!source) {
      setSvg('');
      setError(null);
      return;
    }
    // Debounce 300ms so typing in the editor doesn't re-render every keystroke.
    const timer = setTimeout(async () => {
      try {
        const mermaid = await loadMermaid();
        const id = `mmd-${renderSeq++}`;
        const { svg: out } = await mermaid.render(id, source);
        if (!cancelled) {
          setSvg(out);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Invalid diagram syntax');
          setSvg('');
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [source]);

  return (
    <div
      className="flex flex-col items-center justify-center gap-1.5 overflow-hidden p-1"
      style={fill ? { width: '100%', height: '100%' } : undefined}
    >
      {error ? (
        <div className="flex items-center gap-1.5 rounded-md bg-red-50 px-2.5 py-1.5 text-[11px] text-red-600">
          <AlertTriangle size={13} className="shrink-0" />
          <span className="line-clamp-2">{error}</span>
        </div>
      ) : svg ? (
        <div
          className="mermaid-render flex min-h-0 w-full flex-1 items-center justify-center [&_svg]:h-auto [&_svg]:max-h-full [&_svg]:max-w-full"
          // mermaid output is sanitized (securityLevel: 'strict').
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : (
        <div className="text-[11px] text-fg-subtle">{source ? 'Rendering…' : 'Empty diagram'}</div>
      )}
      {caption && <span className="text-center text-xs text-fg-subtle">{caption}</span>}
    </div>
  );
}
